import {
  OnlyFansLinkType,
  type ProspectAuditInstagramDto,
  type ProspectAuditTikTokDto,
} from '@api'

import { formatViews } from '../pdf-report/utils'
import { type AuditOption, SocialAuditStatus } from './types'

export const INSTAGRAM_AUDIT_OPTIONS: AuditOption<ProspectAuditInstagramDto>[] =
  [
    {
      label: 'Active story',
      description: 'An active story online at the time of the report.',
      conditions: [
        {
          statusLabel: 'No',
          comment: 'Potential loss of subscribers due to perceived inactivity.',
          check: (row) => row.activeHistoryCount === 0,
          status: SocialAuditStatus.Critical,
        },
        {
          statusLabel: 'Yes',
          comment: 'Good for engagement and retention.',
          check: (row) => !!row.activeHistoryCount,
          status: SocialAuditStatus.Good,
        },
      ],
    },
    {
      label: 'Bio length',
      description:
        'To fully utilize the potential of your bio, it should contain at least 150 characters.',
      conditions: [
        {
          statusLabel: 'Under 150 characters',
          comment:
            'Missed opportunity for additional advertising and personality building.',
          check: (row) => !row.bio || row.bio.length < 150,
          status: SocialAuditStatus.Critical,
        },
        {
          statusLabel: 'Over 150 characters',
          comment: 'Good for SEO and personality building.',
          check: (row) => !!row.bio && row.bio.length >= 150,
          status: SocialAuditStatus.Good,
        },
      ],
    },
    {
      label: 'OnlyFans link',
      description:
        'Include your OnlyFans link in your bio but avoid a direct link, which risks an account ban.',
      conditions: [
        {
          statusLabel: 'No link',
          comment: 'A link to your OnlyFans account is crucial for conversion.',
          check: (row) => row.onlyFansLinkType === OnlyFansLinkType.NO_LINK,
          status: SocialAuditStatus.Critical,
        },
        {
          statusLabel: 'Direct link',
          comment: 'A direct link poses a high risk of account ban.',
          check: (row) => row.onlyFansLinkType === OnlyFansLinkType.DIRECT,
          status: SocialAuditStatus.Critical,
        },
        {
          statusLabel: 'External Link',
          comment: 'Good for conversion and community building.',
          check: (row) => row.onlyFansLinkType === OnlyFansLinkType.EXTERNAL,
          status: SocialAuditStatus.Good,
        },
      ],
    },
    {
      label: 'Story Highlights',
      description:
        '5 highlights are perfect for conversion and community building.',
      conditions: [
        {
          dynamicStatusLabel: (row) => `${row.storyHighlightsCount} Highlights`,
          comment: 'Missed opportunity for conversion and community building.',
          check: (row) =>
            !row.storyHighlightsCount || row.storyHighlightsCount < 3,
          status: SocialAuditStatus.Critical,
        },
        {
          dynamicStatusLabel: (row) => `${row.storyHighlightsCount} Highlights`,
          comment: "It's sufficient with potential for improvement.",
          check: (row) =>
            !!row.storyHighlightsCount &&
            row.storyHighlightsCount <= 4 &&
            row.storyHighlightsCount >= 3,
          status: SocialAuditStatus.Warning,
        },
        {
          dynamicStatusLabel: (row) => `${row.storyHighlightsCount} Highlights`,
          comment: 'Good for conversion and community building.',
          check: (row) =>
            !!row.storyHighlightsCount && row.storyHighlightsCount >= 5,
          status: SocialAuditStatus.Good,
        },
      ],
    },
    {
      label: 'Amount of feed posts',
      description:
        'The right amount of posts is crucial for visibility, engagement & credibility, key for a strong online presence.',
      conditions: [
        {
          dynamicStatusLabel: (row) => `${row.feedPostsCount} Feed posts`,
          comment: 'Below average number of posts.',
          check: (row) => !row.feedPostsCount || row.feedPostsCount < 5,
          status: SocialAuditStatus.Critical,
        },
        {
          dynamicStatusLabel: (row) => `${row.feedPostsCount} Feed posts`,
          comment: "It's sufficient with potential for improvement.",
          check: (row) =>
            !!row.feedPostsCount &&
            row.feedPostsCount <= 8 &&
            row.feedPostsCount >= 5,
          status: SocialAuditStatus.Warning,
        },
        {
          dynamicStatusLabel: (row) => `${row.feedPostsCount} Feed posts`,
          comment: 'Good for visibility and engagement.',
          check: (row) => !!row.feedPostsCount && row.feedPostsCount >= 9,
          status: SocialAuditStatus.Good,
        },
      ],
    },
    {
      label: 'Average number of Reel views',
      description:
        'Optimal results are typically achieved with over 70k views.',
      conditions: [
        {
          dynamicStatusLabel: (row) =>
            `${formatViews(row.averageReelViewsCount)} Views`,
          comment: 'Good average number of views.',
          check: (row) =>
            !!row.averageReelViewsCount && row.averageReelViewsCount >= 80000,
          status: SocialAuditStatus.Good,
        },
        {
          dynamicStatusLabel: (row) =>
            `${formatViews(row.averageReelViewsCount)} Views`,
          comment: 'Sufficient with potential for improvement.',
          check: (row) =>
            !!row.averageReelViewsCount && row.averageReelViewsCount >= 70000,
          status: SocialAuditStatus.Warning,
        },
        {
          dynamicStatusLabel: (row) =>
            `${formatViews(row.averageReelViewsCount)} Views`,
          comment: 'Below average number of views.',
          check: (row) =>
            !row.averageReelViewsCount || row.averageReelViewsCount < 60000,
          status: SocialAuditStatus.Critical,
        },
      ],
    },
    {
      label: 'Daily Reel posts',
      description:
        "Posting Reels regularly improves your account's performance.",
      conditions: [
        {
          statusLabel: 'Yes',
          comment: 'Good for account performance.',
          check: (row) => !!row.dailyPosts,
          status: SocialAuditStatus.Good,
        },
        {
          statusLabel: 'No',
          comment:
            "Not posting Reels regularly may hinder your account's performance.",
          check: (row) => !row.dailyPosts,
          status: SocialAuditStatus.Critical,
        },
      ],
    },
  ]

export const TIKTOK_AUDIT_OPTIONS: AuditOption<ProspectAuditTikTokDto>[] = [
  {
    label: 'Instagram name in bio',
    description:
      'Is the Instagram account name present in the bio in the format IG: XXX',
    conditions: [
      {
        statusLabel: 'Yes',
        comment: 'Correct Instagram name in bio.',
        check: (row) => row.instagramNameInBio === true,
        status: SocialAuditStatus.Good,
      },
      {
        statusLabel: 'No',
        comment: 'Missing Instagram name in bio.',
        check: (row) => row.instagramNameInBio === false,
        status: SocialAuditStatus.Critical,
      },
    ],
  },
  {
    label: 'Instagram linked to TikTok',
    description:
      'Link an Instagram account to the TikTok account, to improve conversion.',
    conditions: [
      {
        statusLabel: 'Yes',
        comment: 'Instagram account linked with TikTok.',
        check: (row) => row.instagramLinked === true,
        status: SocialAuditStatus.Good,
      },
      {
        statusLabel: 'No',
        comment: 'Instagram account not linked with TikTok.',
        check: (row) => row.instagramLinked === false,
        status: SocialAuditStatus.Critical,
      },
    ],
  },
  {
    label: 'Instagram funnel navigable from TikTok',
    description:
      'Checking the funnel navigation from TikTok to Instagram and to the main Instagram profiles.',
    conditions: [
      {
        statusLabel: 'Yes',
        comment: 'Navigation is confirmed.',
        check: (row) => row.navigableToInstagram === true,
        status: SocialAuditStatus.Good,
      },
      {
        statusLabel: 'No',
        comment: 'Navigation is not confirmed.',
        check: (row) => row.navigableToInstagram === false,
        status: SocialAuditStatus.Critical,
      },
    ],
  },
  {
    label: 'Daily posts',
    description:
      "Posting TikToks regularly improves your account's performance.",
    conditions: [
      {
        statusLabel: 'Yes',
        comment:
          "Posting TikToks regularly improves your account's performance.",
        check: (row) => row.dailyPosts === true,
        status: SocialAuditStatus.Good,
      },
      {
        statusLabel: 'No',
        comment:
          "Not posting TikToks regularly may hinder your account's performance.",
        check: (row) => row.dailyPosts === false,
        status: SocialAuditStatus.Critical,
      },
    ],
  },
  {
    label: '5 videos posted daily',
    description: 'Post 5 videos every day to ensure account engagement.',
    conditions: [
      {
        statusLabel: 'Yes',
        comment: '5 videos posted every day.',
        check: (row) => !!row.requiredPostNumberDaily,
        status: SocialAuditStatus.Good,
      },
      {
        statusLabel: 'No',
        comment: 'Potential loss of followers due to perceived inactivity.',
        check: (row) => !row.requiredPostNumberDaily,
        status: SocialAuditStatus.Critical,
      },
    ],
  },
  {
    label: 'Average number of views',
    description: 'Optimal results are typically achieved with over 50k views.',
    conditions: [
      {
        dynamicStatusLabel: (row) =>
          `${formatViews(row.averageViewsCount)} Views`,
        comment: 'Good average number of views.',
        check: (row) =>
          !!row.averageViewsCount && row.averageViewsCount >= 50000,
        status: SocialAuditStatus.Good,
      },
      {
        dynamicStatusLabel: (row) =>
          `${formatViews(row.averageViewsCount)} Views`,
        comment: 'Below the recommended 50k views.',
        check: (row) =>
          !!row.averageViewsCount &&
          row.averageViewsCount >= 30000 &&
          row.averageViewsCount < 50000,
        status: SocialAuditStatus.Warning,
      },
      {
        dynamicStatusLabel: (row) =>
          `${formatViews(row.averageViewsCount)} Views`,
        comment: 'Below average number of views.',
        check: (row) => !row.averageViewsCount || row.averageViewsCount < 30000,
        status: SocialAuditStatus.Critical,
      },
    ],
  },
]

export const STATUS_COLORS = {
  [SocialAuditStatus.Good]: '#28A745',
  [SocialAuditStatus.Warning]: '#FFB800',
  [SocialAuditStatus.Critical]: '#D73A49',
}

export const STATUS_POINTS = {
  [SocialAuditStatus.Good]: 1,
  [SocialAuditStatus.Warning]: 0.5,
  [SocialAuditStatus.Critical]: 0,
}

export const PERFORMANCE_BENCHMARKS = {
  [SocialAuditStatus.Good]: 75,
  [SocialAuditStatus.Warning]: 40,
}
